/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React, { useEffect, useMemo, useState } from "react";
import BreakdownCharts from "./BreakdownCharts";
import { Table, Space, Layout } from "antd";
import "../../Styles/styles-Breakdown.css";
import { formattedPlayerName } from "../../Utils/name_utils";
import { 
  getBarColorsForHighcharts,
} from '../../Utils/chart_utils';
import { useSelector } from "react-redux";

function round(num) {
  return Math.round((num + Number.EPSILON) * 10) / 10;
}
function round2(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

export function BreakdownTable(props) {
  const [tableData, setTableData] = useState(null);

  useEffect(() => {
    if (props.data && props.data?.data && props.data?.metrics && props.data?.wellness_metrics) {
      let tableDataTemp = [...props.data.data];

      const topSpeedPercents = props.data["data"]
        .map((row) => topSpeedPercent(row))
        .filter((val) => val !== null);
      const totalTopSpeedPercent = round(
        topSpeedPercents.reduce((acc, curr) => acc + curr, 0)
      );

      tableDataTemp.sort((a, b) => a.order - b.order)

      const newTotalRowObj = [...props.data["metrics"], ...props.data["wellness_metrics"], { key: "total_time" }].reduce(
        (acc, { key }) => {
          const filteredData = props.data["data"]?.filter((row) => EXCLUDE_0_GPS_AVG_METRICS.includes(key) ? row[key] !== 0 : (row[key] !== undefined && row[key] !== null));
          
          const avg =
            filteredData.reduce((acc, curr) => acc + (curr[key] || 0), 0);
          
          const rounded_avg = round(avg);
          acc[key] = isNaN(rounded_avg) ? 0 : rounded_avg ;
          return acc;
        },
        { calculated_top_speed_percent: totalTopSpeedPercent }
      );

      
      newTotalRowObj["name"] = <span style={{fontWeight: 480}}>Total</span>;
      newTotalRowObj["total_time"] = <span style={{fontWeight: 480}}>{newTotalRowObj["total_time"]}</span>;
      
      setTableData([newTotalRowObj, ...tableDataTemp]);
    }
  }, [])

  const {
    info: {
      EXCLUDE_0_GPS_AVG_METRICS
    }
  } = useSelector(({ team }) => team);

  const topSpeedPercent = (row) => {
    const div = props.data.player_colors.find(
      (e) => e.player_id === row.player_id
    )?.["top_speed"];
    return div ? (100 * row["top_speed"]) / div : null;
  };

  const averages = useMemo(() => {
    const topSpeedPercents = props.data["data"]
      .map((row) => topSpeedPercent(row))
      .filter((val) => val !== null);
    const avgTopSpeedPercent = round(
      topSpeedPercents.reduce((acc, curr) => acc + curr, 0) /
        topSpeedPercents.length
    );

    return [...props.data["metrics"], ...props.data["wellness_metrics"], { key: "total_time" }].reduce(
      (acc, { key }) => {
        const filteredData = props.data["data"]?.filter((row) => EXCLUDE_0_GPS_AVG_METRICS.includes(key) ? row[key] !== 0 : (row[key] !== undefined && row[key] !== null));
        
        const avg =
          filteredData.reduce((acc, curr) => acc + (curr[key] || 0), 0) /
          filteredData.length;
        
        const rounded_avg = round(avg);
        acc[key] = isNaN(rounded_avg) ? 0 : rounded_avg ;
        return acc;
      },
      { calculated_top_speed_percent: avgTopSpeedPercent + "%" }
    );
  }, [props.data]);

  const renderColumn =
    (columnKey = "calculated_top_speed_percent") =>
    (value_in, row) => {
      let percentValue = null;
      let key = columnKey;
      if (columnKey === "calculated_top_speed_percent") {
        percentValue = topSpeedPercent(row);
        key = "top_speed";
      }

      let content =
        percentValue == null ? round(value_in) : round(percentValue) + "%";
      if (props.team_name === "Purdue Football" && columnKey === "top_speed") {
        content = round2(value_in);
      }

      let html, style;
      if (typeof row?.name === 'string') {
        style = {};
        const barColor = getBarColorsForHighcharts(row, key, null, null, props.dataColorUtil, row.player_id)
        style["color"] = barColor;
        html = <span style={style}>{content}</span>;
      }
      else {
        style = {};
        style["fontWeight"] = 480;
        html = <span style={style}>{content}</span>;
      }

      return html;
    };

  let columns = [
    {
      title: "Name",
      key: "name",
      children: [
        {
          title: "Average",
          dataIndex: "name",
        },
      ],
      sorter: {
        compare: (a, b) => a?.name.localeCompare(b?.name),
        multiple: 1,
      },
      render: (name) => (
        <span>{formattedPlayerName(props?.team_name, name)}</span>
      ),
    },
    {
      title: "Position",
      key: "position",
      children: [
        {
          title: "",
          dataIndex: "position",
        },
      ],
      sorter: {
        compare: (a, b) => a?.position.localeCompare(b?.position),
        multiple: 1,
      },
      render: (position) => <span>{position}</span>,
    }
  ];


  if (props.data?.total_time) {
    columns.push({
      title: "Total Time",
      key: "total_time",
      sorter: {
        compare: (a, b) => a?.total_time - b?.total_time,
        multiple: 2,
      },
      children: [
        {
          title: averages["total_time"],
          dataIndex: "total_time",
        },
      ],
      render: (total_time) => <span>{round(total_time)}</span>,
    });
  }

  let top_speed_percent_exists = false;
  if (props.data?.metrics && props.data.metrics.length > 0) {
    top_speed_percent_exists = props.data.metrics.some(item => item.key === "top_speed_percent");
  }
  for (let i = 0; i < props.data.metrics.length; ++i) {
    let metric = props.data.metrics[i];
    let metric_name = metric.name;
    let key = metric.key;

    if (props.hideDistanceZones) {
      if (metric_name === "Distance") {
        metric_name = "Total " + metric_name;
      }
    }
    if (props.team_name === "Orlando Pride") {
      if (metric_name === "Acceleration" || metric_name === "Deceleration") {
        metric_name += " Z4-Z6";
      }
    }

    columns.push({
      title: metric_name,
      dataIndex: key,
      children: [
        {
          title: averages[key],
          dataIndex: key,
          render: renderColumn(key),
        },
      ],
      sorter: {
        compare: (a, b) => a[key] - b[key],
        multiple: columns.length,
      },
    });
    if (key === "top_speed" && !top_speed_percent_exists) {
      columns.push({
        title: "Top Speed %",
        children: [
          {
            title: averages["calculated_top_speed_percent"],
            dataIndex: "calculated_top_speed_percent",
            key: "calculated_top_speed_percent",
            render: renderColumn(),
          },
        ],
        sorter: {
          compare: (a, b) => {
            return topSpeedPercent(a) - topSpeedPercent(b);
          },
          multiple: columns.length,
        },
      });
    }
  }

  return (
    <>
      {(props.data?.data || []).length > 0 ? (
        <>
          <div className="breakdown-table">
            <Space>
              <Table
                rowKey="Id"
                columns={
                  columns
                }
                style={{width: '99vw'}}
                scroll={{ x: '99vw' }}
                size="small"
                dataSource={tableData}
                className="exportable gpsdv-table breakdown-table table-scrollbar"
                rowClassName={(row, index) =>
                  props.showAll && "ant-table-row-no-shadow"
                }
                pagination={
                  props.showAll
                    ? false
                    : {
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ["5", "10", "20", "30"],
                        position: ["topLeft"],
                      }
                }
                loading={props.loading}
              />
            </Space>
          </div>
          {/* <div className="legend-container">
            <div className="legends-wrapper">
              <span className="legend-icon-green"></span>
              <div> 35% &lt; of Game Baselines</div>
            </div>

            <div className="legends-wrapper">
              <span className="legend-icon-yellow"></span>
              <div> 35-65% of Game Baselines</div>
            </div>

            <div className="legends-wrapper">
              <span className="legend-icon-red"></span>
              <div> &gt;65% of Game Baselines</div>
            </div>
          </div> */}

          <BreakdownCharts
            data={props.chartData}
            hideDistanceZones={props.hideDistanceZones}
            team_name={props.team_name}
            showAll={props.showAll}
            team_colors={props.team_colors}
            dataColorUtil={props.dataColorUtil}
          />
        </>
      ) : (
        <p>No data for your query.</p>
      )}
    </>
  );
}
